import { useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DOMPurify from 'dompurify';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';
import EnquiryForm from '../components/EnquiryForm';
import RevealProvider from '../components/RevealProvider';
import { COUNTRIES, getCountry, formatUSD } from '../data/countries';

const SAFE_HTML = { ALLOWED_TAGS: ['em', 'strong', 'br', 'span'], ALLOWED_ATTR: [] };
const safe = (html) => ({ __html: DOMPurify.sanitize(html ?? '', SAFE_HTML) });

const SLOTS = ['a', 'b', 'c'];
const SLOT_LABELS = { a: 'Country A', b: 'Country B', c: 'Country C' };

// Metric definitions: [key, label, formatter, "winner" comparator (smaller-is-better, larger-is-better, truthy-is-better, none)]
const METRICS = [
  { key: 'visaName', label: 'Visa programme', fmt: (c) => c.visaName, cmp: 'none' },
  { key: 'minIncome', label: 'Min income (single)', fmt: (c) => `${formatUSD(c.minIncome)}/mo`, cmp: 'min', val: (c) => c.minIncome },
  { key: 'incomeSpouse', label: 'Min income (family)', fmt: (c) => `${formatUSD(c.incomeSpouse)}/mo`, cmp: 'min', val: (c) => c.incomeSpouse },
  { key: 'processing', label: 'Processing time', fmt: (c) => c.processing, cmp: 'min', val: (c) => c.processingWeeks },
  { key: 'validity', label: 'Validity', fmt: (c) => c.validity, cmp: 'max', val: (c) => c.validityYears },
  { key: 'renewable', label: 'Renewable?', fmt: (c) => (c.renewable ? 'Yes' : 'No'), cmp: 'truthy', val: (c) => c.renewable },
  { key: 'taxFriendly', label: 'Tax-friendly?', fmt: (c) => (c.taxFriendly ? 'Yes' : 'No'), cmp: 'truthy', val: (c) => c.taxFriendly },
  { key: 'currency', label: 'Currency', fmt: (c) => c.currency, cmp: 'none' },
  { key: 'capital', label: 'Capital', fmt: (c) => c.capital, cmp: 'none' },
  { key: 'languages', label: 'Languages', fmt: (c) => (c.languages || []).join(', '), cmp: 'none' },
  { key: 'region', label: 'Region', fmt: (c) => c.regionLabel, cmp: 'none' },
];

function computeWinners(countries, metric) {
  if (metric.cmp === 'none') return new Set();
  const valid = countries.filter(Boolean);
  if (valid.length < 2) return new Set();
  const vals = valid.map((c) => ({ slug: c.slug, v: metric.val(c) }));
  if (metric.cmp === 'min') {
    const m = Math.min(...vals.map((x) => x.v));
    return new Set(vals.filter((x) => x.v === m).map((x) => x.slug));
  }
  if (metric.cmp === 'max') {
    const m = Math.max(...vals.map((x) => x.v));
    return new Set(vals.filter((x) => x.v === m).map((x) => x.slug));
  }
  if (metric.cmp === 'truthy') {
    return new Set(vals.filter((x) => x.v).map((x) => x.slug));
  }
  return new Set();
}

function bestForTags(country, allCountries) {
  if (!country) return [];
  const tags = [];
  const valid = allCountries.filter(Boolean);
  if (valid.length < 2) return tags;
  const minIncome = Math.min(...valid.map((c) => c.minIncome));
  const minWeeks = Math.min(...valid.map((c) => c.processingWeeks));
  const maxYears = Math.max(...valid.map((c) => c.validityYears));
  if (country.minIncome === minIncome) tags.push('Lowest income');
  if (country.processingWeeks === minWeeks) tags.push('Fastest processing');
  if (country.validityYears === maxYears) tags.push('Longest validity');
  if (country.taxFriendly) tags.push('Tax-friendly');
  if (country.renewable) tags.push('Renewable');
  return tags;
}

export default function Compare() {
  const [params, setParams] = useSearchParams();
  const selected = SLOTS.map((s) => params.get(s)).map((slug) => (slug ? getCountry(slug) : null));
  const filledCount = selected.filter(Boolean).length;

  // First-load default: Portugal vs Spain vs Croatia, only if no params at all
  useEffect(() => {
    if (filledCount === 0) {
      setParams({ a: 'portugal', b: 'spain', c: 'croatia' }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSlot = (slot) => (e) => {
    const next = new URLSearchParams(params);
    const v = e.target.value;
    if (v) next.set(slot, v);
    else next.delete(slot);
    setParams(next, { replace: true });
  };

  const dedupeOptions = (slot) => {
    const usedElsewhere = SLOTS.filter((s) => s !== slot)
      .map((s) => params.get(s))
      .filter(Boolean);
    return COUNTRIES.filter((c) => !usedElsewhere.includes(c.slug));
  };

  const titleCountries = selected.filter(Boolean).map((c) => c.name).join(' vs ');
  const pageTitle = titleCountries
    ? `${titleCountries} — Digital Nomad Visa Comparison | Nomad Compass`
    : 'Compare Digital Nomad Visas Side-by-Side | Nomad Compass';
  const pageDesc = titleCountries
    ? `Side-by-side comparison of ${titleCountries} digital nomad visas — income, processing, validity, tax, and the real all-in cost.`
    : 'Compare up to three digital nomad visa programmes side-by-side. Income, processing, validity, and tax — at a glance.';
  const pageUrl = `https://nomadcompasshub.com/compare${titleCountries ? '?' + params.toString() : ''}`;

  const enquiryCountries = selected.filter(Boolean).map((c) => c.name).join(', ');
  const enquiryPageSource = `compare${titleCountries ? `:${selected.filter(Boolean).map((c) => c.slug).join('+')}` : ''}`;

  const winnersByMetric = useMemo(
    () => METRICS.map((m) => ({ key: m.key, winners: computeWinners(selected, m) })).reduce((acc, x) => ({ ...acc, [x.key]: x.winners }), {}),
    [selected]
  );

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://nomadcompasshub.com/' },
      { '@type': 'ListItem', position: 2, name: 'Compare visas', item: 'https://nomadcompasshub.com/compare' },
    ],
  };

  return (
    <RevealProvider>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={pageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>

      <TopBar variant="page" breadcrumb="Compare" />

      <section className="compare-hero">
        <div className="compare-hero-inner">
          <div className="moat-eyebrow">Compare visas <span className="pill">Side-by-side</span></div>
          <h1 className="compare-h1" data-testid="compare-h1">
            Two or three programmes. <em>One decision.</em>
          </h1>
          <p className="compare-lead">
            Pick up to three digital nomad visas. We line up the numbers — income, processing, validity, tax — so you can stop juggling tabs and start seeing the trade-offs.
          </p>
        </div>
      </section>

      <section className="compare-pickers" aria-label="Country pickers">
        <div className="compare-pickers-inner">
          {SLOTS.map((slot) => {
            const c = selected[SLOTS.indexOf(slot)];
            return (
              <div key={slot} className="compare-picker reveal" data-testid={`compare-picker-${slot}`}>
                <label className="compare-picker-label">{SLOT_LABELS[slot]}</label>
                <select
                  className="compare-picker-select"
                  value={c?.slug || ''}
                  onChange={setSlot(slot)}
                  data-testid={`compare-select-${slot}`}
                >
                  <option value="">— None —</option>
                  {dedupeOptions(slot).map((co) => (
                    <option key={co.slug} value={co.slug}>
                      {co.name} · {co.visaName}
                    </option>
                  ))}
                </select>
                {c && (
                  <div className="compare-picker-flag">
                    <span aria-hidden="true">{c.flag}</span>
                    <Link to={`/countries/${c.slug}`} className="compare-picker-link" data-testid={`compare-link-${slot}`}>
                      Read full {c.name} guide →
                    </Link>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {filledCount === 0 && (
        <section className="compare-empty">
          <p>Pick at least one country above to get started, or <Link to="/countries">browse all 41 programmes →</Link></p>
        </section>
      )}

      {filledCount >= 1 && (
        <section className="compare-table-wrap" id="comparison" aria-label="Comparison table">
          <div className="compare-table-inner">
            {/* Best-for tag header row */}
            <div className="compare-row compare-row-tags reveal">
              <div className="compare-cell compare-cell-label">Best for</div>
              {selected.map((c, i) => (
                <div key={SLOTS[i]} className="compare-cell" data-testid={`compare-tags-${SLOTS[i]}`}>
                  {c ? (
                    <div className="compare-tags">
                      {bestForTags(c, selected).length === 0 ? (
                        <span className="compare-tag compare-tag-muted">—</span>
                      ) : (
                        bestForTags(c, selected).map((t) => (
                          <span key={t} className="compare-tag">{t}</span>
                        ))
                      )}
                    </div>
                  ) : (
                    <span className="compare-cell-empty">—</span>
                  )}
                </div>
              ))}
            </div>

            {/* Country header row */}
            <div className="compare-row compare-row-head reveal">
              <div className="compare-cell compare-cell-label" />
              {selected.map((c, i) => (
                <div key={SLOTS[i]} className="compare-cell compare-cell-head">
                  {c ? (
                    <>
                      <div className="compare-head-flag" aria-hidden="true">{c.flag}</div>
                      <div className="compare-head-name">{c.name}</div>
                      <div className="compare-head-sub">{c.regionLabel}</div>
                    </>
                  ) : (
                    <div className="compare-head-name compare-cell-empty">No country selected</div>
                  )}
                </div>
              ))}
            </div>

            {/* Metric rows */}
            {METRICS.map((m) => (
              <div key={m.key} className="compare-row reveal" data-testid={`compare-row-${m.key}`}>
                <div className="compare-cell compare-cell-label">{m.label}</div>
                {selected.map((c, i) => {
                  if (!c) return <div key={SLOTS[i]} className="compare-cell"><span className="compare-cell-empty">—</span></div>;
                  const isWinner = winnersByMetric[m.key]?.has(c.slug);
                  return (
                    <div key={SLOTS[i]} className={`compare-cell compare-cell-value ${isWinner ? 'compare-cell-winner' : ''}`}>
                      {isWinner && <span className="compare-winner-mark" aria-label="Best in this row">✓</span>}
                      <span>{m.fmt(c)}</span>
                    </div>
                  );
                })}
              </div>
            ))}

            {/* CTAs row */}
            <div className="compare-row compare-row-ctas reveal">
              <div className="compare-cell compare-cell-label">Take action</div>
              {selected.map((c, i) => (
                <div key={SLOTS[i]} className="compare-cell">
                  {c ? (
                    <Link
                      to={`/countries/${c.slug}#enquiry`}
                      className="btn-primary compare-cta-btn"
                      data-testid={`compare-cta-${SLOTS[i]}`}
                    >
                      Apply for {c.name} →
                    </Link>
                  ) : (
                    <span className="compare-cell-empty">—</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {filledCount >= 1 && (
        <section className="compare-enq" id="enquiry">
          <div className="compare-enq-grid">
            <div className="compare-enq-left reveal">
              <div className="moat-eyebrow">Still undecided?</div>
              <h2 dangerouslySetInnerHTML={safe(
                filledCount > 1
                  ? `Tell us about <em>all ${filledCount} options</em> at once.`
                  : `Tell us about <em>your ${selected.find(Boolean).name} plan.</em>`
              )} />
              <p>
                We&rsquo;ll review your situation, weigh the trade-offs across the {filledCount > 1 ? 'programmes you&rsquo;re considering' : 'programme'}, and reply within one working day with the option we&rsquo;d actually recommend — and why.
              </p>
              <div className="enq-promises">
                <div className="enq-promise">A named expert replies personally — no chatbots.</div>
                <div className="enq-promise">If none of these fit you, we&rsquo;ll tell you what does.</div>
                <div className="enq-promise">Your information is never shared with third parties.</div>
              </div>
            </div>
            <EnquiryForm pageSource={enquiryPageSource} variant="home" defaultCountry={enquiryCountries} />
          </div>
        </section>
      )}

      <Footer />
    </RevealProvider>
  );
}
